import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useToast } from '../../../../../components/common/Toast';
import { useAccessTheme } from '../useAccessTheme';
import { EntraRadioOption, EntraSection, EntraToggleRow, EntraCheckbox } from './EntraWizardUi';
import {
  getEntraSyncConfig,
  listEntraGroups,
  previewEntraSync,
  saveEntraSyncConfig,
  type EntraDiscoveredGroup,
  type EntraSyncConfig,
  type EntraSyncPreview,
} from './entraConnectionService';

interface EntraSyncStepProps {
  connectionId: string;
}

const defaultConfig = (connectionId: string): EntraSyncConfig => ({
  connectionId,
  syncEnabled: true,
  syncUsers: true,
  syncGroups: true,
  syncGroupMemberships: true,
  userScope: 'allUsers',
  selectedGroupIds: [],
  attributes: {
    firstName: true,
    lastName: true,
    email: true,
    department: true,
    jobTitle: true,
    manager: true,
    employeeId: true,
    officeLocation: true,
    accountStatus: true,
  },
  autoCreateUsers: true,
  defaultPortalAccessStatus: 'none',
  existingUserMatchMode: 'linkByEmail',
  disabledUserAction: 'updateStatusOnly',
  deletedUserAction: 'markDeleted',
});

const EntraSyncStep: React.FC<EntraSyncStepProps> = ({ connectionId }) => {
  const t = useAccessTheme();
  const { showToast } = useToast();
  const [config, setConfig] = useState<EntraSyncConfig>(() => defaultConfig(connectionId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<EntraSyncPreview | null>(null);
  const [saved, setSaved] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [groups, setGroups] = useState<EntraDiscoveredGroup[]>([]);
  const [knownGroups, setKnownGroups] = useState<Record<string, EntraDiscoveredGroup>>({});
  const [groupsLoading, setGroupsLoading] = useState(false);

  const patch = (partial: Partial<EntraSyncConfig>) => setConfig((prev) => ({ ...prev, ...partial }));

  useEffect(() => {
    let active = true;
    setLoading(true);
    getEntraSyncConfig(connectionId)
      .then((data) => {
        if (active) setConfig(data);
      })
      .catch((err: Error) => {
        if (active) showToast({ type: 'error', message: err.message });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [connectionId, showToast]);

  useEffect(() => {
    if (config.userScope !== 'selectedGroups' || !connectionId) return;
    let active = true;
    const timer = window.setTimeout(() => {
      setGroupsLoading(true);
      listEntraGroups(connectionId, groupSearch || undefined)
        .then((data) => {
          if (!active) return;
          setGroups(data);
          setKnownGroups((prev) => {
            const next = { ...prev };
            data.forEach((group) => {
              next[group.id] = group;
            });
            return next;
          });
        })
        .catch((err: Error) => {
          if (active) showToast({ type: 'error', message: err.message });
        })
        .finally(() => {
          if (active) setGroupsLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [config.userScope, connectionId, groupSearch, showToast]);

  const toggleGroup = (id: string) => {
    const selected = config.selectedGroupIds.includes(id)
      ? config.selectedGroupIds.filter((item) => item !== id)
      : [...config.selectedGroupIds, id];
    patch({ selectedGroupIds: selected });
  };

  const save = async () => {
    setSaving(true);
    try {
      const next = await saveEntraSyncConfig(connectionId, config);
      setConfig(next);
      setSaved(true);
      setPreview(null);
      showToast({ type: 'success', message: 'Sync configuration saved. Synchronization was not started.' });
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Unable to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      setPreview(await previewEntraSync(connectionId));
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Unable to preview sync' });
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return <p className={`text-sm ${t.muted}`}>Loading sync configuration...</p>;
  }

  return (
    <>
      <EntraSection title="Sync Configuration" description="Choose what to synchronize from Microsoft Entra ID.">
        <EntraToggleRow
          label="Directory Synchronization"
          checked={config.syncEnabled}
          onChange={(syncEnabled) => patch({ syncEnabled })}
        />
      </EntraSection>

      <EntraSection title="Users">
        <div className="space-y-2">
          <EntraRadioOption
            name="userScope"
            value="allUsers"
            checked={config.userScope === 'allUsers'}
            title="All users"
            onChange={(userScope) => patch({ userScope })}
          />
          <EntraRadioOption
            name="userScope"
            value="selectedGroups"
            checked={config.userScope === 'selectedGroups'}
            title="Users from selected groups"
            onChange={(userScope) => patch({ userScope })}
          />
        </div>
      </EntraSection>

      {config.userScope === 'selectedGroups' ? (
        <EntraSection title="Select Entra Groups" description="Groups are listed for configuration only. They are not imported until sync runs.">
          <div className="relative mb-3">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${t.muted}`} />
            <input
              type="text"
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder="Search groups"
              className={'w-full border rounded-lg pl-9 pr-3 py-2 text-sm outline-none ' + t.input}
              autoComplete="off"
            />
          </div>
          {groupsLoading ? <p className={`text-sm ${t.muted}`}>Loading groups...</p> : null}
          <div className="space-y-2">
            {[
              ...config.selectedGroupIds
                .filter((id) => !groups.some((group) => group.id === id))
                .map((id) => knownGroups[id] || { id, displayName: id, description: 'Selected group', memberCount: 0 }),
              ...groups,
            ].map((group) => (
              <label key={group.id} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${t.border}`}>
                <span className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={config.selectedGroupIds.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                  />
                  <span>
                    <span className={`block ${t.title}`}>{group.displayName}</span>
                    {group.description ? <span className={`block text-xs ${t.muted}`}>{group.description}</span> : null}
                  </span>
                </span>
                {group.memberCount ? <span className={`text-xs ${t.muted}`}>{group.memberCount.toLocaleString()}</span> : null}
              </label>
            ))}
            {!groupsLoading && groups.length === 0 && config.selectedGroupIds.length === 0 ? (
              <p className={`text-sm ${t.muted}`}>No groups found.</p>
            ) : null}
          </div>
        </EntraSection>
      ) : null}

      <EntraSection title="Directory Data">
        <div className="space-y-2">
          <EntraCheckbox label="Users" checked={config.syncUsers} onChange={(syncUsers) => patch({ syncUsers })} />
          <EntraCheckbox label="Groups" checked={config.syncGroups} onChange={(syncGroups) => patch({ syncGroups })} />
          <EntraCheckbox
            label="Group memberships"
            checked={config.syncGroupMemberships}
            onChange={(syncGroupMemberships) => patch({ syncGroupMemberships })}
          />
        </div>
      </EntraSection>

      <EntraSection title="User Attributes">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <EntraCheckbox
            label="First name"
            checked={config.attributes.firstName}
            onChange={(firstName) => patch({ attributes: { ...config.attributes, firstName } })}
          />
          <EntraCheckbox
            label="Last name"
            checked={config.attributes.lastName}
            onChange={(lastName) => patch({ attributes: { ...config.attributes, lastName } })}
          />
          <EntraCheckbox
            label="Email"
            checked={config.attributes.email}
            onChange={(email) => patch({ attributes: { ...config.attributes, email } })}
          />
          <EntraCheckbox
            label="Department"
            checked={config.attributes.department}
            onChange={(department) => patch({ attributes: { ...config.attributes, department } })}
          />
          <EntraCheckbox
            label="Job title"
            checked={config.attributes.jobTitle}
            onChange={(jobTitle) => patch({ attributes: { ...config.attributes, jobTitle } })}
          />
          <EntraCheckbox
            label="Manager"
            checked={config.attributes.manager}
            onChange={(manager) => patch({ attributes: { ...config.attributes, manager } })}
          />
          <EntraCheckbox
            label="Employee ID"
            checked={config.attributes.employeeId}
            onChange={(employeeId) => patch({ attributes: { ...config.attributes, employeeId } })}
          />
          <EntraCheckbox
            label="Office location"
            checked={config.attributes.officeLocation}
            onChange={(officeLocation) => patch({ attributes: { ...config.attributes, officeLocation } })}
          />
          <EntraCheckbox
            label="Account status"
            checked={config.attributes.accountStatus}
            onChange={(accountStatus) => patch({ attributes: { ...config.attributes, accountStatus } })}
          />
        </div>
      </EntraSection>

      <EntraSection title="New Users">
        <EntraToggleRow
          label="Create users automatically"
          checked={config.autoCreateUsers}
          onChange={(autoCreateUsers) => patch({ autoCreateUsers })}
        />
        <div className="mt-4 space-y-2">
          <p className={`text-xs font-medium ${t.muted}`}>Portal access for new users</p>
          <EntraRadioOption
            name="portalAccess"
            value="none"
            checked={config.defaultPortalAccessStatus === 'none'}
            title="No portal access"
            onChange={() => patch({ defaultPortalAccessStatus: 'none' })}
          />
          <EntraRadioOption
            name="portalAccess"
            value="active"
            checked={config.defaultPortalAccessStatus === 'active'}
            title="Grant portal access"
            onChange={() => patch({ defaultPortalAccessStatus: 'active' })}
          />
        </div>
      </EntraSection>

      <EntraSection title="Existing Users">
        <div className="space-y-2">
          <EntraRadioOption
            name="existingMatch"
            value="linkByEmail"
            checked={config.existingUserMatchMode === 'linkByEmail'}
            title="Link matching Selfic users by email"
            onChange={() => patch({ existingUserMatchMode: 'linkByEmail' })}
          />
          <EntraRadioOption
            name="existingMatch"
            value="manualReview"
            checked={config.existingUserMatchMode === 'manualReview'}
            title="Require manual review"
            onChange={() => patch({ existingUserMatchMode: 'manualReview' })}
          />
        </div>
      </EntraSection>

      <EntraSection title="Disabled Entra Users">
        <div className="space-y-2">
          <EntraRadioOption
            name="disabledAction"
            value="updateStatusOnly"
            checked={config.disabledUserAction === 'updateStatusOnly'}
            title="Update directory status only"
            onChange={() => patch({ disabledUserAction: 'updateStatusOnly' })}
          />
          <EntraRadioOption
            name="disabledAction"
            value="suspendNonAdmins"
            checked={config.disabledUserAction === 'suspendNonAdmins'}
            title="Suspend non-admin portal access"
            onChange={() => patch({ disabledUserAction: 'suspendNonAdmins' })}
          />
        </div>
      </EntraSection>

      <EntraSection title="Deleted Entra Users">
        <div className="space-y-2">
          <EntraRadioOption
            name="deletedAction"
            value="markDeleted"
            checked={config.deletedUserAction === 'markDeleted'}
            title="Mark directory identity as deleted"
            onChange={() => patch({ deletedUserAction: 'markDeleted' })}
          />
          <EntraRadioOption
            name="deletedAction"
            value="suspendNonAdmins"
            checked={config.deletedUserAction === 'suspendNonAdmins'}
            title="Suspend non-admin portal access"
            onChange={() => patch({ deletedUserAction: 'suspendNonAdmins' })}
          />
        </div>
      </EntraSection>

      {preview ? (
        <EntraSection title="Configuration Preview" description="This preview reads Microsoft Entra and writes nothing.">
          <p className={`text-sm ${t.body}`}>
            {preview.usersDiscovered.toLocaleString()} users
            <br />
            {preview.groupsDiscovered.toLocaleString()} groups
            <br />
            {preview.groupMembershipsDiscovered.toLocaleString()} memberships
            <br />
            {preview.disabledUsers.toLocaleString()} disabled users
          </p>
          <p className={`text-sm mt-3 ${t.body}`}>
            {preview.existingUsersMatched.toLocaleString()} existing Selfic users will be linked.
            <br />
            {preview.newUsers.toLocaleString()} new managed identities will be created.
          </p>
          <p className={`text-xs mt-3 ${t.muted}`}>No existing roles or access settings will be changed.</p>
          {preview.note ? <p className={`text-xs mt-2 ${t.muted}`}>{preview.note}</p> : null}
        </EntraSection>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        {saved ? (
          <button
            type="button"
            onClick={runPreview}
            disabled={previewing || saving}
            className={'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ' + t.btnSecondary}
          >
            {previewing ? 'Previewing...' : 'Preview'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className={'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ' + t.btnPrimary}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </>
  );
};

export default EntraSyncStep;
